import { useEffect, useState } from 'react';

import {
  BackrsForm,
  InfoModal,
  ContentNugget,
  BackrsFormValues,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { taskDialogFormConfig } from '@backrs_ui/grow-ui/src/pages/Priorities/configs/taskDialogForm.config';
import {
  ContentSearchAutocompleteResult,
  useLazyFetchContentSearchAutocompleteOptionsQuery,
} from '@backrs_ui/grow-ui/src/redux/contentSearch/contentSearch.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  selectCurrentPlaylistId,
  openSecondaryNewPlaylistDialog,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import {
  PriorityTaskPost,
  PriorityTaskType,
  PriorityTask,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Theme, SxProps, Typography, Button } from '@mui/material';
import { useForm } from 'react-hook-form';

import {
  useDetermineNuggetTaskTypeId,
  usePlaylistChangeHandler,
  useTransformTagsForEdit,
} from '../hooks/tasks.hooks';
import {
  constructTaskForPostPut,
  determineTaskContentCode,
  determineContentCodeFromTask,
  getTaskDialogDefaultValues,
} from '../utils/tasks.utils';

const TASK_DIALOG_STRINGS = appStrings.modules.priorities.taskDialog;

type TaskDialogProps = Readonly<{
  playlists: PriorityPlaylist[];
  taskTypes: PriorityTaskType[];
  nuggetToAdd: ContentNugget | null;
  taskToEdit?: PriorityTask | null;
  selectedPlaylist?: PriorityPlaylist | null;
  preloadedTaskInfo?: Partial<PriorityTask>;
  handleClose: (shouldClosePlaylistDrawer: boolean) => void;
  handleTaskCreate: (task: Partial<PriorityTaskPost>) => Promise<void>;
  handleTaskUpdate?: (task: Partial<PriorityTaskPost>) => Promise<void>;
}>;

export const TaskDialog = ({
  playlists,
  taskTypes,
  nuggetToAdd,
  taskToEdit,
  selectedPlaylist,
  preloadedTaskInfo,
  handleClose,
  handleTaskCreate,
  handleTaskUpdate,
}: TaskDialogProps) => {
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<ContentSearchAutocompleteResult[]>(
    [],
  );

  const [triggerFetchAutocomplete, { data: autocompleteData }] =
    useLazyFetchContentSearchAutocompleteOptionsQuery();

  const handleInputChange = (input: string) => {
    triggerFetchAutocomplete({ searchTerm: input });
  };

  // Update local autocomplete options when new API data arrives
  useEffect(() => {
    if (autocompleteData && Array.isArray(autocompleteData) && autocompleteData.length) {
      const newOptions = autocompleteData.map((data) => {
        const existingOption = autoCompleteOptions.find(
          (option) => option.endpoint === data.endpoint,
        );

        return {
          ...data,
          searchTerm: existingOption ? existingOption.searchTerm : '',
        };
      });

      if (JSON.stringify(newOptions) !== JSON.stringify(autoCompleteOptions)) {
        setAutoCompleteOptions(newOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteData]);

  const currentPlaylistId = useAppSelector(selectCurrentPlaylistId);
  const dispatch = useAppDispatch();

  // Get current playlist from the playlists prop using the ID from Redux, or use selectedPlaylist if provided
  const currentlySelectedPlaylist =
    selectedPlaylist ||
    (currentPlaylistId
      ? playlists.find((playlist) => playlist.id === currentPlaylistId) || null
      : null);

  const nuggetTaskTypeId = useDetermineNuggetTaskTypeId(nuggetToAdd);

  // Determine content code from nugget or preloaded task info
  const nuggetContentCode = determineTaskContentCode(nuggetToAdd);

  const preloadedContentCode =
    !nuggetContentCode && preloadedTaskInfo
      ? determineContentCodeFromTask(preloadedTaskInfo)
      : undefined;
  const finalContentCode = nuggetContentCode || preloadedContentCode;

  // Create form methods first with a temporary default type ID
  const methods = useForm<BackrsFormValues>({
    defaultValues: getTaskDialogDefaultValues({
      taskToEdit,
      nuggetToAdd,
      currentlySelectedPlaylist,
      defaultTypeId: '', // Temporary, will be updated after fields are created
      preloadedTaskInfo,
    }),
  });

  const fields = taskDialogFormConfig({
    playlists,
    taskTypes,
    nuggetToAdd,
    nuggetTaskTypeId,
    contentCode: finalContentCode,
    taskToEdit,
    preloadedTaskInfo,
    handleOpeningPlaylistDialog: () => {
      dispatch(openSecondaryNewPlaylistDialog(undefined));
    },
    autoCompleteOptions,
    setAutoCompleteOptions,
    handleInputChange,
    formMethods: methods,
  });

  const typeIdField = fields.find((field) => field.name === 'type_code');
  const defaultTypeId = typeIdField?.defaultValue?.toString() || '';

  // Update the form with the correct defaultTypeId if it changed
  useEffect(() => {
    if (defaultTypeId && defaultTypeId !== methods.getValues('type_code')) {
      methods.setValue('type_code', defaultTypeId);
    }
  }, [defaultTypeId, methods]);

  useTransformTagsForEdit(methods, taskToEdit, preloadedTaskInfo);

  const isFormStateValid = methods.formState.isValid;
  const isEditing = !!taskToEdit;

  // Watch for playlist_id changes and update the redux slice with the selected playlist based on Redux state
  usePlaylistChangeHandler({
    playlistId: methods.watch('playlist_id'),
    playlists,
    shouldPlaylistDrawerChange: !isEditing,
  });

  const handleSubmit = async (data: BackrsFormValues) => {
    try {
      const constructedTask = constructTaskForPostPut(data);

      if (isEditing && handleTaskUpdate) {
        await handleTaskUpdate(constructedTask);
        handleClose(false);

        return;
      }

      await handleTaskCreate(constructedTask);
      handleClose(false);
    } catch (e) {
      console.log('Error saving task', e);
    }
  };

  // When a playlist is created from the secondary dialog, update the form's playlist_id
  useEffect(() => {
    if (selectedPlaylist) {
      // Use type assertion to set value due to BackrsFormValues typing
      (methods.setValue as (name: string, value: unknown) => void)(
        'playlist_id',
        selectedPlaylist.id.toString(),
      );
    }
  }, [selectedPlaylist, methods]);

  return (
    <InfoModal
      open={true}
      width="sm"
      onClose={() => handleClose(false)}
      hideCircleImage
      featuredIcon={{
        iconName: 'Flag05',
        size: 'lg',
        color: 'Colors/Foreground/fg-secondary',
      }}
      headerInLineWithIcon
      showBottomBorder
      dialogSx={dialogSx}
      headerContent={
        <Box>
          <Typography variant="text-lg-semibold">
            {isEditing ? TASK_DIALOG_STRINGS.editTitle : TASK_DIALOG_STRINGS.title}
          </Typography>
          <Typography variant="text-sm-regular">
            {isEditing ? TASK_DIALOG_STRINGS.editDescription : TASK_DIALOG_STRINGS.description}
          </Typography>
        </Box>
      }
      bodyContainerSx={bodyContainerSx}
      bodyContent={
        <Box>
          <Box sx={formContainerSx}>
            <BackrsForm fields={fields} onSubmit={handleSubmit} methods={methods} />
          </Box>

          <Box sx={buttonContainerSx}>
            <Button
              id="task-dialog-cancel-button"
              variant="contained"
              hierarchy="Secondary gray"
              fullWidth
              size="lg"
              onClick={() => handleClose(false)}
            >
              {TASK_DIALOG_STRINGS.cancel}
            </Button>
            <Button
              id="task-dialog-submit-button"
              variant="contained"
              hierarchy="Primary"
              fullWidth
              size="lg"
              onClick={() => handleSubmit(methods.getValues())}
              disabled={!isFormStateValid}
            >
              {isEditing ? TASK_DIALOG_STRINGS.updateTask : TASK_DIALOG_STRINGS.addTask}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-none'],
  marginX: -backrs.spacing['spacing-3xl'],
  width: '100%',
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-2xl'],
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  p: backrs.spacing['spacing-3xl'],
});

const formContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
});

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '640px', // specific pixel width to match Figma
    width: '640px',
    margin: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-4xl'] },
  },
});
